/**
 * Los conceptos de **fecha manual**: los que no se liquidan por el mes del
 * período sino por un intervalo que escribe quien liquida.
 *
 * El caso son la preventa de DYLASA (01150 y 01154) y el supermercado (01188):
 * su ciclo no coincide con el mes. Cuáles son lo decide el servidor —vienen
 * marcados en el resumen de conceptos, y se configuran desde el diccionario—;
 * acá solo se decide cuándo se pueden consultar y tildar.
 *
 * La fecha es **una sola y vale para todos**: se escribe una vez en la banda y
 * viaja con la consulta, el Excel y el impacto.
 */

import type { ResumenConcepto } from '../../api';
import { aDdMmAaaa } from '../../lib/fechas';
import { rangoLegible } from './seleccion';

/** Los conceptos que piden una fecha manual, estén tildados o no. */
export const conceptosManuales = (
  conceptos: readonly ResumenConcepto[],
): readonly ResumenConcepto[] => conceptos.filter((c) => c.rangoPropio);

/** Los que se liquidan por el mes, que son los de la tabla principal. */
export const conceptosDelMes = (
  conceptos: readonly ResumenConcepto[],
): readonly ResumenConcepto[] => conceptos.filter((c) => !c.rangoPropio);

/**
 * Qué anda mal en el intervalo, o `null` si se puede consultar.
 *
 * Devuelve el motivo y no un booleano: el botón queda deshabilitado y hay que
 * poder decir por qué, que si no parece que la pantalla se trabó.
 */
export function problemaDelRango(desde: string, hasta: string): string | null {
  if (desde === '' || hasta === '') return 'Falta una de las dos fechas.';
  const dia = /^\d{4}-\d{2}-\d{2}$/;
  if (!dia.test(desde) || !dia.test(hasta)) return 'Alguna de las fechas está incompleta.';
  if (hasta < desde) return 'El rango termina antes de empezar.';
  // Mismo tope que el servidor: un año es siempre el año mal tipeado.
  const dias = (Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000;
  if (dias > 365) return 'El rango abarca más de un año. Revisar el año de las fechas.';
  return null;
}

/**
 * Si un concepto se puede tildar.
 *
 * Uno de fecha manual sin fecha consultada no trae datos: tildarlo mostraría
 * un cero en la previsualización y mandaría al impacto una novedad que el
 * servidor va a trabar.
 */
export const sePuedeTildar = (concepto: ResumenConcepto, conFechaManual: boolean): boolean =>
  !concepto.rangoPropio || conFechaManual;

/** "01/09 – 30/09/2026", para contar en pantalla de qué fechas salió un total. */
export const textoDeRango = (rango: { desde: string; hasta: string }): string =>
  rangoLegible(aDdMmAaaa(rango.desde), aDdMmAaaa(rango.hasta));
